import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, MapPin, Search, Users, MoreHorizontal, Bookmark } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, apiFetch, handleApiError, getActivityFeedPreferences, updateCurrentUser, API_URL } from '../lib/api';
import { ACTIVITY_LABEL, ActivityType } from '../lib/activityTypes';
import { DataSource, DATA_SOURCE_LABEL } from '../lib/dataSources';
import { useActivityCatalog, ActivityDefinition } from '../lib/activityCatalog';
import Logo from './common/Logo';
import { useGeolocation } from '../utils/useGeolocation';
import { useWebSocketEvent } from '../utils/websocket';
import Toast from './common/Toast';
import DataSourceBadge from './common/DataSourceBadge';
import ActiveSearchModal, { SavedSearch, SearchForm } from './ActiveSearchModal';
import PhoneNumberInput, { isValidPhoneNumber } from './common/PhoneNumberInput';

const ACTIVITY_COLOR: Record<ActivityType, string> = {
  DRILLING: 'bg-orange-500',
  COMPLETION: 'bg-blue-500',
  PRODUCTION: 'bg-green-500',
  INTERVENTION: 'bg-red-500',
};

type Activity = {
  id: string;
  type: ActivityType;
  activity: string;
  description: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  location?: string;
  wellId?: string | null;
  createdAt: string;
  company?: string;
  well?: { id: string; name: string; location?: string; company?: string };
  wellName?: string;
  photo?: string;
  metadata?: { fileIds?: string[]; [key: string]: any };
  time?: string;
  content?: string;
  source?: DataSource;
  isSaved?: boolean;
};

type NotificationItem = {
  id: string;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  createdAt: string;
};

type NotificationPrefs = {
  push: boolean;
  realTimeAlerts: boolean;
  dailySummary: boolean;
  weeklyReport: boolean;
};


type UserPreferences = {
  hasOnboarded?: boolean;
  hasCompletedTour?: boolean;
  language?: string;
  [key: string]: any;
};

type User = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'USER' | 'ADMIN';
  company?: string;
  phone?: string;
  emailVerifiedAt?: string | null;
  isActive: boolean;
  allowedToPost: boolean;
  preferences?: UserPreferences;
};

type ReportForm = {
  activityDefinitionId: string;
  activityType: ActivityType;
  location: string;
  description: string;
  photo: string | null;
  photoFileId: string | null;
  well: string;
  company: string;
  source: DataSource;
};

type ApiCallOptions = Omit<RequestInit, 'body'> & { body?: any };

const normalizeActivityTypes = (types: string[] = []): ActivityType[] =>
  types.filter((t): t is ActivityType => (Object.keys(ACTIVITY_LABEL) as ActivityType[]).includes(t as ActivityType));

export const filterActivities = (
  activities: Activity[],
  activeTab: string,
  matchesSavedSearch: (activity: Activity) => boolean
): Activity[] => {
  if (activeTab === 'For you') {
    const preferred = getActivityFeedPreferences();
    return activities.filter(
      activity =>
        preferred.includes(activity.type) || matchesSavedSearch(activity)
    );
  }
  return activities;
};

const WellsiteActivityFeed = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('All Activity');
  const [showReportModal, setShowReportModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedActivityToShare, setSelectedActivityToShare] = useState<Activity | null>(null);
  const [profileActiveTab, setProfileActiveTab] = useState('Profile');
  const [savedPosts, setSavedPosts] = useState<Set<string>>(new Set());
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(1);
  const [selectedActivityTypes, setSelectedActivityTypes] = useState<Set<ActivityType>>(new Set());
  const { status: locationStatus, coords, request } = useGeolocation(showReportModal);
  const [isListening, setIsListening] = useState(false);
  const [speechRecognition, setSpeechRecognition] = useState<any>(null);
  const searchButtonRef = useRef<HTMLButtonElement | null>(null);
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' } | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [searchDefaults, setSearchDefaults] = useState<Partial<SearchForm> | null>(null);

  const [reportForm, setReportForm] = useState<ReportForm>({
    activityDefinitionId: '',
    activityType: 'DRILLING',
    location: '',
    description: '',
    photo: null,
    photoFileId: null,
    well: '',
    company: '',
    source: 'FIELD_REPORT'
  });


  const [activities, setActivities] = useState<Activity[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const { catalog } = useActivityCatalog();
  const fieldReportActivities = (catalog || []).filter(d => d.sources.includes('FIELD_REPORT'));
  const groupedFieldReportActivities = fieldReportActivities.reduce<Record<ActivityType, ActivityDefinition[]>>(
    (acc, def) => {
      (acc[def.type] ||= []).push(def);
      return acc;
    },
    {} as Record<ActivityType, ActivityDefinition[]>
  );

  useEffect(() => {
    if (!reportForm.activityDefinitionId && fieldReportActivities.length) {
      const first = fieldReportActivities[0];
      setReportForm(prev => ({ ...prev, activityDefinitionId: first.id, activityType: first.type }));
    }
  }, [fieldReportActivities]);

  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPrefs>({
    push: false,
    realTimeAlerts: false,
    dailySummary: false,
    weeklyReport: false,
  });
  const [language, setLanguage] = useState('English');

  type ProfileForm = {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    company: string;
    allowedToPost: boolean;
  };

  const [profileForm, setProfileForm] = useState<ProfileForm>({
    firstName: '',
    lastName: '',
    email: '',
    phone: undefined,
    company: '',
    allowedToPost: false,
  });

  const [profilePhoneError, setProfilePhoneError] =
    useState<string | undefined>();

  useEffect(() => {
    if (!user?.allowedToPost) setShowReportModal(false);
  }, [user?.allowedToPost]);

  useEffect(() => {
    if (showProfileModal) {
      const prefs = user?.preferences || {};
      const notifPrefs = prefs.notifications || {};
      setNotificationPrefs({
        push: !!notifPrefs.push,
        realTimeAlerts: !!notifPrefs.realTimeAlerts,
        dailySummary: !!notifPrefs.dailySummary,
        weeklyReport: !!notifPrefs.weeklyReport,
      });
      setLanguage(prefs.language || 'English');
    }
  }, [showProfileModal, user?.preferences]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mapActivity = (activity: Activity): Activity => {
    const fileId = activity.metadata?.fileIds?.[0];
    return {
      ...activity,
      photo: fileId ? `${API_URL}/files/${fileId}/download?size=medium` : undefined,
    };
  };

  useWebSocketEvent('new_activity', (activity: Activity) => {
    const mapped = mapActivity(activity);
    setActivities(prev =>
      prev.some(a => a.id === mapped.id) ? prev : [mapped, ...prev]
    );
  });

  useWebSocketEvent(
    'email_verified',
    (data: { emailVerifiedAt?: string }) => {
      setUser(prev => {
        if (!prev) {
          (async () => {
            try {
              const res = await apiCall('/auth/me');
              const fetched = res?.data?.user || null;
              if (fetched) {
                const updated = {
                  ...fetched,
                  emailVerifiedAt:
                    data?.emailVerifiedAt || new Date().toISOString(),
                };
                localStorage.setItem('user', JSON.stringify(updated));
                setUser(updated);
              }
            } catch (err) {
              console.error(
                'Failed to refresh user after email verification',
                err
              );
            }
          })();
          return prev;
        }
        const updated = {
          ...prev,
          emailVerifiedAt:
            data?.emailVerifiedAt || new Date().toISOString(),
        };
        localStorage.setItem('user', JSON.stringify(updated));
        return updated;
      });
    },
    [user]
  );

  const matchesSavedSearch = (activity: Activity): boolean => {
    if (!savedSearches.length) return false;
    const text = `${activity.activity} ${activity.description} ${activity.location} ${activity.wellName || activity.well?.name || ''}`.toLowerCase();
    return savedSearches.some(search => {
      if (search.isActive === false) return false;
      if (search.type !== 'activities' && search.type !== 'all') return false;
      const query = (search.query || '').toLowerCase();
      const filters = search.filters || {};
      const queryMatch = !query || text.includes(query);
      const typeMatch = !filters.activityType || activity.type === filters.activityType;
      const locationMatch = !filters.location || (activity.location || '').toLowerCase().includes(String(filters.location).toLowerCase());
      const wellMatch =
        !filters.well ||
        (activity.wellName || activity.well?.name || '')
          .toLowerCase()
          .includes(String(filters.well).toLowerCase());
      return queryMatch && typeMatch && locationMatch && wellMatch;
    });
  };

  const handleOnboardingComplete = async () => {
    if (selectedActivityTypes.size < 2) {
      alert('Please select at least 2 activity types to continue.');
      return;
    }
    const prefs = {
      hasOnboarded: true,
      activityFeed: Array.from(selectedActivityTypes),
    };
    try {
      await savePreferences(prefs);

      // Propagate activity types to local state so preferences and
      // checkboxes immediately reflect the user's choices
      setUser(prev =>
        prev
          ? {
              ...prev,
              preferences: {
                ...(prev.preferences || {}),
                activityFeed: prefs.activityFeed,
                hasOnboarded: true,
              },
            }
          : prev
      );
      setSelectedActivityTypes(new Set(prefs.activityFeed));

      // Re-fetch preferences to ensure UI reflects the latest server state
      try {
        const prefRes = await apiCall('/users/me/preferences');
        const refreshed = prefRes?.data?.preferences || {};
        if (refreshed.activityFeed) {
          refreshed.activityFeed = normalizeActivityTypes(refreshed.activityFeed);
        }
        setUser(prev =>
          prev
            ? { ...prev, preferences: { ...(prev.preferences || {}), ...refreshed } }
            : prev
        );
        if (refreshed.activityFeed) {
          setSelectedActivityTypes(new Set(refreshed.activityFeed));
        }
      } catch (prefErr) {
        console.error('Failed to refresh preferences', prefErr);
      }

      setShowOnboarding(false);
      setShowTour(true);
      setTourStep(1);
      setPage(1);
      await loadActivities(1);
    } catch (e) {
      console.error('Failed to update onboarding preference', e);
    }
  };

  const toggleActivityType = (activityType: ActivityType) => {
    setSelectedActivityTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(activityType)) {
        newSet.delete(activityType);
      } else {
        newSet.add(activityType);
      }
      return newSet;
    });
  };

  const handleTourNext = async () => {
    if (tourStep === 1) {
      setTourStep(2);
    } else {
      await savePreferences({ hasCompletedTour: true });
      setShowTour(false);
    }
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);


  const apiCall = useCallback(
    async (endpoint: string, options: ApiCallOptions = {}) => {
      try {
        const { data } = await apiFetch(endpoint, options);
        return data;
      } catch (err: any) {
        if (err.status === 401) {
          navigate('/login', { replace: true });
        }
        throw err;
      }
    },
    [navigate]
  );

  useEffect(() => {
    const checkVerificationStatus = async () => {
      if (user && !user.emailVerifiedAt) {
        try {
          const res = await apiCall('/auth/me');
          const refreshed = res?.data?.user;
          if (refreshed?.emailVerifiedAt) {
            updateCurrentUser(refreshed);
            setUser(refreshed);
          }
        } catch (err) {
          console.error('Failed to refresh user after email verification', err);
        }
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkVerificationStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    checkVerificationStatus();
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.emailVerifiedAt]);

  const savePreferences = async (prefs: UserPreferences) => {
    const activityFeed = prefs.activityFeed
      ? normalizeActivityTypes(prefs.activityFeed)
      : undefined;
    const mergedPrefs: UserPreferences = {
      ...(user?.preferences || {}),
      ...prefs,
      ...(activityFeed ? { activityFeed } : {}),
      hasOnboarded:
        prefs.hasOnboarded ?? user?.preferences?.hasOnboarded ?? false,
    };

    const res = await apiCall('/users/me/preferences', {
      method: 'PUT',
      body: mergedPrefs,
    });

    const serverPrefs = res?.data?.preferences || mergedPrefs;

    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, preferences: serverPrefs } as User;
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });

    return res;
  };

  const handleNotificationToggle = (
    key: keyof NotificationPrefs
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const updated = { ...notificationPrefs, [key]: e.target.checked };
    setNotificationPrefs(updated);
    savePreferences({ notifications: updated });
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const lang = e.target.value;
    setLanguage(lang);
    savePreferences({ language: lang });
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const me = await apiCall('/auth/me');
        const authedUser: User = me?.data?.user;
        if (!authedUser || !authedUser.isActive) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          navigate('/login', { replace: true });
          return;
        }

        let prefs: UserPreferences = {};
        try {
          const prefRes = await apiCall('/users/me/preferences');
          prefs = prefRes?.data?.preferences || {};
          if (prefs.activityFeed) {
            prefs.activityFeed = normalizeActivityTypes(prefs.activityFeed);
          }
        } catch (prefErr) {
          console.error('Failed to fetch preferences', prefErr);
        }

        const userWithPrefs = {
          ...authedUser,
          preferences: { ...(authedUser.preferences || {}), ...prefs },
        };
        localStorage.setItem('user', JSON.stringify(userWithPrefs));
        setUser(userWithPrefs);
        setLanguage(prefs.language || 'English');
        setSelectedActivityTypes(new Set(prefs.activityFeed || []));
        setShowOnboarding(!prefs.hasOnboarded);
        const shouldShowTour = !!prefs.hasOnboarded && !prefs.hasCompletedTour;
        setShowTour(shouldShowTour);
        if (shouldShowTour) {
          setTourStep(1);
        }

        const activityParams = new URLSearchParams({ page: '1', limit: '20' });

        const [acts, savedActs, notifs, unread, searches] = await Promise.all([
          apiCall(`/activities?${activityParams.toString()}`),
          apiCall(`/activities/saved`),
          apiCall(`/notifications?limit=10`),
          apiCall(`/notifications/unread/count`),
          apiCall(`/searches/saved`)
        ]);

        const initialActivities: Activity[] = (acts?.data?.activities || []).map(
          (a: Activity) => mapActivity(a)
        );
        setActivities(initialActivities);
        setSavedPosts(new Set(savedActs?.data?.activities?.map((a: Activity) => a.id) || []));
        setHasMore((initialActivities.length || 0) === 20);
        setPage(1);

        setNotifications(notifs?.data?.notifications || []);
        setUnreadCount(unread?.data?.unreadCount || 0);

        setSavedSearches(searches?.data?.savedSearches || []);

        setProfileForm({
          firstName: authedUser.firstName || '',
          lastName: authedUser.lastName || '',
          email: authedUser.email || '',
          phone: authedUser.phone || '',
          company: authedUser.company || '',
          allowedToPost: authedUser.allowedToPost || false
        });
      } catch (e: any) {
        console.error(e);
        setError(handleApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [apiCall, navigate]);

  const loadActivities = async (nextPage?: number, filters: Record<string, any> = {}) => {
    const pageToLoad = nextPage ?? page + 1;
    const params = new URLSearchParams({
      page: String(pageToLoad),
      limit: '20',
      ...Object.fromEntries(Object.entries(filters).map(([k, v]) => [k, String(v)]))
    });

    let res;
    if (activeTab === 'Saved') {
      res = await apiCall(`/activities/saved?${params.toString()}`);
      const savedIds = (res?.data?.activities || []).map((a: Activity) => a.id);
      setSavedPosts(new Set(savedIds));
  } else {
    res = await apiCall(`/activities?${params.toString()}`);
    const savedIds = (res?.data?.activities || [])
      .filter((a: Activity) => a.isSaved)
      .map((a: Activity) => a.id);
    if (savedIds.length) {
      setSavedPosts(prev => new Set([...Array.from(prev), ...savedIds]));
    }
  }
  const newItems: Activity[] = (res?.data?.activities || []).map(
    (a: Activity) => mapActivity(a)
  );
  setActivities(prev => (pageToLoad === 1 ? newItems : [...prev, ...newItems]));
  setPage(pageToLoad);
  setHasMore(newItems.length === 20);
};

  const refreshNotifications = async () => {
    const [notifs, unread] = await Promise.all([
      apiCall(`/notifications?limit=10`),
      apiCall(`/notifications/unread/count`)
    ]);
    setNotifications(notifs?.data?.notifications || []);
    setUnreadCount(unread?.data?.unreadCount || 0);
  };

  const handleResendVerification = async () => {
    if (!user) return;
    try {
      await apiCall('/auth/resend-verification', {
        method: 'POST',
        body: { email: user.email },
      });
      setToast({ message: 'Verification email sent.', type: 'success' });
      setResendCooldown(60);
    } catch (e: any) {
      setToast({ message: handleApiError(e), type: 'error' });
    }
  };

  const markNotificationAsRead = async (notificationId: string) => {
    await apiCall(`/notifications/${notificationId}/read`, { method: 'PUT' });
    await refreshNotifications();
  };

  const markAllNotificationsAsRead = async () => {
    await apiCall(`/notifications/read-all`, { method: 'PUT' });
    await refreshNotifications();
  };

  const loadSavedSearches = async () => {
    const res = await apiCall('/searches/saved');
    setSavedSearches(res?.data?.savedSearches || []);
  };

  const toggleSavedSearchStatus = async (searchId: string, isActive?: boolean) => {
    await apiCall(`/searches/saved/${searchId}`, { method: 'PUT', body: { isActive: !isActive } });
    setSavedSearches(prev => prev.map(s => s.id === searchId ? { ...s, isActive: !isActive } : s));
  };

  const isInitialTabLoad = useRef(true);
  useEffect(() => {
    if (isInitialTabLoad.current) {
      isInitialTabLoad.current = false;
      return;
    }
    loadActivities(1);
  }, [activeTab]);

  const handleReportSubmit = async () => {
    if (!user?.allowedToPost) {
      setToast({ message: 'Access denied. Posting permission required.', type: 'error' });
      return;
    }
    if (!reportForm.description || !reportForm.company || !reportForm.well) {
      setToast({ message: 'Company and well are required.', type: 'error' });
      return;
    }
    if (user && user.role !== 'ADMIN' && !user.emailVerifiedAt) {
      setToast({ message: 'Please verify your email to submit a report.', type: 'error' });
      return;
    }
    if (locationStatus !== 'granted') {
      setToast({ message: 'Enable location to submit.', type: 'error' });
      return;
    }
    const body: any = {
      activityDefinitionId: reportForm.activityDefinitionId,
      type: reportForm.activityType,
      description: reportForm.description,
      severity: 'MEDIUM',
      source: reportForm.source
    };
    if (reportForm.location) body.location = reportForm.location;
    if (reportForm.well) body.well = reportForm.well;
    if (reportForm.company) body.company = reportForm.company;
    if (reportForm.photoFileId) body.metadata = { fileIds: [reportForm.photoFileId] };
    await apiCall('/activities', { method: 'POST', body });
    await loadActivities(1);
    const defaultDef = fieldReportActivities[0];
    setReportForm({
      activityDefinitionId: defaultDef ? defaultDef.id : '',
      activityType: defaultDef ? defaultDef.type : 'DRILLING',
      location: '',
      description: '',
      photo: null,
      photoFileId: null,
      well: '',
      company: '',
      source: 'FIELD_REPORT'
    });
    request();
    if (speechRecognition && isListening) speechRecognition.stop();
    setIsListening(false);
    setShowReportModal(false);
  };

  const validateProfilePhone = () => {
    if (!profileForm.phone) {
      setProfileForm(prev => ({ ...prev, phone: undefined }));
      setProfilePhoneError(undefined);
      return true;
    }
    if (isValidPhoneNumber(profileForm.phone)) {
      setProfilePhoneError(undefined);
      return true;
    } else {
      setProfilePhoneError('Invalid phone number');
      return false;
    }
  };

  const handleProfileUpdate = async () => {
    if (!validateProfilePhone() || !user?.id) return;
    await apiCall(`/users/${user.id}`, { method: 'PUT', body: profileForm });
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...profileForm } as User;
      updateCurrentUser(updated);
      return updated;
    });
  };

  const handleLogout = async () => {
    try {
      await apiCall('/auth/logout', { method: 'POST' });
    } catch {
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      navigate('/login', { replace: true });
    }
  };

  // Initialize speech recognition on component mount
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        
        recognition.onresult = (event: any) => {
          let finalTranscript = '';
          let interimTranscript = '';
          
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript;
            } else {
              interimTranscript += transcript;
            }
          }
          
          if (finalTranscript) {
            setReportForm(prev => ({
              ...prev,
              description: prev.description + finalTranscript
            }));
          }
        };
        
        recognition.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
        };
        
        recognition.onend = () => {
          setIsListening(false);
        };
        
        setSpeechRecognition(recognition);
      }
    }
    }, []);

    useEffect(() => {
      if (locationStatus === 'granted' && coords) {
        handleInputChange('location', `${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`);
      } else if (locationStatus === 'denied') {
        handleInputChange('location', '');
      }
    }, [locationStatus, coords]);

    const toggleVoiceRecognition = () => {
    if (!speechRecognition) {
      alert('Voice recognition is not supported in your browser. Please try Chrome or Safari.');
      return;
    }
    
    if (isListening) {
      speechRecognition.stop();
      setIsListening(false);
    } else {
      speechRecognition.start();
      setIsListening(true);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const img = document.createElement('img');
        img.src = URL.createObjectURL(file);
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const maxSize = 1920;
          let { width, height } = img;
          if (width > height && width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          } else if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          canvas.toBlob(async blob => {
            if (blob) {
              const compressedFile = new File([blob], file.name, { type: 'image/webp' });
              const reader = new FileReader();
              reader.onload = () => {
                setReportForm(prev => ({
                  ...prev,
                  photo: reader.result as string
                }));
              };
              reader.readAsDataURL(compressedFile);

              const formData = new FormData();
              formData.append('files', compressedFile, file.name.replace(/\.[^.]+$/, '.webp'));
              try {
                const { data } = await api.files.upload(formData);
                const id = data?.data?.files?.[0]?.id || data?.data?.fileIds?.[0];
                if (id) {
                  setReportForm(prev => ({ ...prev, photoFileId: id }));
                }
              } catch (err) {
                setToast({ message: handleApiError(err), type: 'error' });
              }
            }
          }, 'image/webp', 0.8);
        };
      } catch (err) {
        setToast({ message: handleApiError(err), type: 'error' });
      }
    }
  };

  const removePhoto = () => {
    setReportForm(prev => ({
      ...prev,
      photo: null,
      photoFileId: null
    }));
  };

  const handleInputChange = (field: string, value: string) => {
    setReportForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleActivityChange = (id: string) => {
    const def = fieldReportActivities.find(d => d.id === id);
    setReportForm(prev => ({
      ...prev,
      activityDefinitionId: id,
      activityType: def ? def.type : prev.activityType
    }));
  };


  const toggleSavePost = async (postId: string) => {
    const isSaved = savedPosts.has(postId);
    try {
      if (isSaved) {
        await apiCall(`/activities/${postId}/save`, { method: 'DELETE' });
      } else {
        await apiCall(`/activities/${postId}/save`, { method: 'POST' });
      }
      setSavedPosts(prev => {
        const newSet = new Set(prev);
        if (isSaved) {
          newSet.delete(postId);
        } else {
          newSet.add(postId);
        }
        return newSet;
      });
      if (isSaved && activeTab === 'Saved') {
        setActivities(prev => prev.filter(a => a.id !== postId));
      }
    } catch (e) {
      console.error('Toggle save error', e);
    }
  };


  const getPriorityColor = (severity: Activity['severity']) => {
    switch (severity) {
      case 'CRITICAL': return 'text-red-500';
      case 'HIGH': return 'text-orange-500';
      case 'MEDIUM': return 'text-yellow-500';
      case 'LOW': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  const getActivityTag = (type: ActivityType) => {
    return {
      text: ACTIVITY_LABEL[type] || 'Activity',
      color: 'bg-gray-700'
    };
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((+now - +date) / 60000);
    if (diffInMinutes < 1) return 'now';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return `${Math.floor(diffInMinutes / 1440)}d`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4" />
          <p className="text-gray-400">Connecting to backend...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-400 mb-4">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-2">Backend Connection Failed</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <div className="space-y-2">
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors"
            >
              Retry Connection
            </button>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const geolocationAvailable = typeof navigator !== 'undefined' && !!navigator.geolocation;
  const canSubmit =
    geolocationAvailable &&
    locationStatus === 'granted' &&
    reportForm.location &&
    reportForm.description &&
    reportForm.company &&
    reportForm.well;
  const filteredActivities = filterActivities(
    activities,
    activeTab,
    matchesSavedSearch
  );

  const shareActivityTag = selectedActivityToShare
    ? getActivityTag(selectedActivityToShare.type)
    : null;
  const shareCompanyName =
    selectedActivityToShare?.company || selectedActivityToShare?.well?.company;
  const shareWellName =
    selectedActivityToShare?.wellName || selectedActivityToShare?.well?.name;
  return (
    <div className="min-h-screen bg-white text-gray-900 dark:bg-black dark:text-white">
      {/* Onboarding Modal */}
      {showOnboarding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="relative bg-black border border-gray-700 rounded-2xl w-full max-w-md mx-4">
            <div className="p-6">
              <h2 className="text-lg font-medium text-white mb-4 text-center">
                What are you interested in?
              </h2>
              
              <div className="flex flex-wrap gap-3 mb-6">
                {(Object.keys(ACTIVITY_LABEL) as ActivityType[]).map((value) => (
                  <button
                    key={value}
                    onClick={() => toggleActivityType(value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      selectedActivityTypes.has(value)
                        ? 'bg-white text-black shadow-lg transform scale-105'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:scale-105'
                    }`}
                  >
                    {ACTIVITY_LABEL[value]}
                  </button>
                ))}
              </div>

              <button
                onClick={handleOnboardingComplete}
                disabled={selectedActivityTypes.size < 2}
                className={`w-full px-4 py-3 rounded-lg font-medium transition-colors ${
                  selectedActivityTypes.size >= 2
                    ? 'bg-white text-black hover:bg-gray-200'
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                }`}
              >
                Continue ({selectedActivityTypes.size}/4 selected)
              </button>
              
              <p className="text-gray-400 text-xs text-center mt-3">
                Select at least 2 categories to personalize your experience
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Guided Tour */}
      {showTour && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/80 p-6 text-center">
          <div>
            {tourStep === 1 && (
              <>
                <h2 className="text-xl font-semibold mb-4">Welcome to your feed</h2>
                <p className="mb-6 text-gray-300">
                  Scroll to explore the latest activity from your wells.
                </p>
              </>
            )}
            {tourStep === 2 && (
              <>
                <h2 className="text-xl font-semibold mb-4">Create a search</h2>
                <p className="mb-6 text-gray-300">
                  Tap the blue search button to build and save custom searches.
                </p>
              </>
            )}
            <button
              onClick={handleTourNext}
              className="px-4 py-2 bg-white text-black rounded-md"
            >
              {tourStep === 1 ? 'Next' : 'Finish'}
            </button>
          </div>
        </div>
      )}

      {!showOnboarding && (
        <>
          {/* Header */}
          <div className="sticky top-0 z-50 bg-white/80 border-b border-gray-200 backdrop-blur-md dark:bg-black/80 dark:border-gray-800">
            <div className="max-w-2xl mx-auto px-4">
              <div className="flex items-center justify-between h-14">
                <Logo className="h-6 w-auto" />
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <button
                      onClick={() => setShowNotificationsModal(true)}
                      className="p-2 rounded-full transition-colors hover:bg-gray-100 dark:hover:bg-gray-900"
                    >
                      <Bell className="w-5 h-5 text-gray-900 dark:text-white" />
                    </button>
                    {(unreadCount > 0 || (user && !user.emailVerifiedAt)) && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-medium">
                        {unreadCount > 0 ? unreadCount : ''}
                      </span>
                    )}
                  </div>
                  <div
                    onClick={() => setShowProfileModal(true)}
                    className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm cursor-pointer hover:bg-blue-600 transition-colors"
                  >
                    {user?.firstName?.[0]?.toUpperCase() || 'U'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-2xl mx-auto">
            {/* Navigation Tabs */}
            <div className="flex border-b border-gray-200 sticky top-14 bg-white/80 backdrop-blur-md z-40 dark:border-gray-800 dark:bg-black/80">
              {['All Activity', 'For you', 'Saved'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-4 text-sm font-medium relative transition-colors ${
                    activeTab === tab
                      ? 'text-gray-900 dark:text-white'
                      : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {tab}
                  {activeTab === tab && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-blue-500 rounded-full"></div>
                  )}
                </button>
              ))}
            </div>

            {/* Activity Feed */}
            <div className="divide-y divide-gray-800">
              {filteredActivities.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-500 text-sm">No posts yet</p>
                </div>
              ) : (
                filteredActivities.map((activity) => {
                const activityTag = getActivityTag(activity.type);
                const companyName = activity.company || activity.well?.company;
                const wellName = activity.wellName || activity.well?.name;

                return (
                <div key={activity.id} className="p-4 hover:bg-gray-950/50 transition-colors cursor-pointer">
                  <div className="flex space-x-3">
                    <div className="flex-1 min-w-0">
                      {/* Header with Tag */}
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <DataSourceBadge source={activity.source} />
                        <span
                          className={`inline-flex flex-shrink-0 items-center px-3 py-1 rounded-full text-xs font-medium text-white whitespace-nowrap ${activityTag.color}`}
                        >
                          {activityTag.text}
                        </span>
                        {companyName && (
                          <span className="inline-flex flex-shrink-0 items-center px-3 py-1 rounded-full text-xs font-medium text-white bg-gray-700 whitespace-nowrap">
                            {companyName}
                          </span>
                        )}
                        {wellName && (
                          <span className="inline-flex flex-shrink-0 items-center px-3 py-1 rounded-full text-xs font-medium text-white bg-gray-700 whitespace-nowrap">
                            {wellName}
                          </span>
                        )}
                        <span className="text-gray-500">·</span>
                        <span className="text-gray-500 hover:underline cursor-pointer">{formatTimeAgo(activity.createdAt)}</span>
                        <div className={`w-2 h-2 rounded-full ${getPriorityColor(activity.severity).replace('text-', 'bg-')}`}></div>
                      </div>

                      {/* Content */}
                      <div className="mb-3">
                        {activity.activity && activity.activity !== ACTIVITY_LABEL[activity.type] && (
                          <h3 className="text-white font-medium mb-1">{activity.activity}</h3>
                        )}
                        <p className="text-white leading-normal whitespace-pre-wrap">{activity.description}</p>
                      </div>

                      {/* Photo if present */}
                      {activity.photo && (
                        <div className="mb-3">
                          {(() => {
                            const url = activity.photo.replace('?size=medium', '');
                            return (
                              <img
                                src={`${url}?size=medium`}
                                srcSet={`${url}?size=thumb 400w, ${url}?size=medium 800w, ${url} 1920w`}
                                sizes="(max-width: 768px) 100vw, 768px"
                                loading="lazy"
                                className="w-full h-auto rounded-lg border border-gray-700"
                                alt="Field report"
                                onError={e => {
                                  const img = e.currentTarget;
                                  if (img.src.includes('?size=thumb') || img.src.includes('?size=medium')) {
                                    img.onerror = null;
                                    img.src = url;
                                  }
                                }}
                              />
                            );
                          })()}
                        </div>
                      )}

                      {/* Location & Source */}
                      <div className="flex items-center space-x-4 text-gray-500 text-sm mb-3">
                        {activity.location && (
                          <div className="flex items-center space-x-1">
                            <MapPin className="w-4 h-4" />
                            <span>{activity.location}</span>
                          </div>
                        )}
                        {activity.well && (
                          <div className="flex items-center space-x-1">
                            <span>Well: {activity.well.name}</span>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => toggleSavePost(activity.id)}
                          className={`flex items-center space-x-2 rounded-lg px-3 py-2 transition-colors text-sm font-medium ${
                            savedPosts.has(activity.id)
                              ? 'text-white bg-gray-800'
                              : 'text-gray-500 hover:text-white hover:bg-gray-800'
                          }`}
                        >
                          <Bookmark className={`w-4 h-4 ${savedPosts.has(activity.id) ? 'fill-current' : ''}`} />
                          <span>{savedPosts.has(activity.id) ? 'Unsave' : 'Save'}</span>
                        </button>

                        <button
                          onClick={() => {
                            setSelectedActivityToShare(activity);
                            setShowShareModal(true);
                          }}
                          className="flex items-center space-x-2 text-gray-500 hover:text-green-400 hover:bg-green-400/10 rounded-lg px-3 py-2 transition-colors text-sm font-medium"
                        >
                          <Users className="w-4 h-4" />
                          <span>Share</span>
                        </button>

                        <button
                          onClick={() => {
                            setSearchDefaults({
                              searchName: activity.activity || '',
                              activityType: activity.type,
                              location: activity.location || '',
                              company: activity.company || activity.well?.company || '',
                              well: activity.wellName || activity.well?.name || '',
                              channel: 'For you',
                            });
                            setShowSearchModal(true);
                          }}
                          className="flex items-center space-x-2 text-gray-500 hover:text-blue-400 hover:bg-blue-400/10 rounded-lg px-3 py-2 transition-colors text-sm font-medium"
                        >
                          <Search className="w-4 h-4" />
                          <span>More like this</span>
                        </button>

                      </div>
                    </div>

                    {/* More Options */}
                    <button className="p-2 hover:bg-gray-800 rounded-full transition-colors opacity-0 group-hover:opacity-100">
                      <MoreHorizontal className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                </div>
                );
              })
              )}
            </div>

            {/* Load More / End Message */}
            {filteredActivities.length === 0 ? null : (
              <div className="p-4 text-center">
                <span className="text-gray-500 text-sm">
                  All caught up
                </span>
              </div>
            )}
          </div>

          {/* Floating Action Buttons */}
          <div className="fixed bottom-6 right-6 flex flex-col space-y-3">
            {user?.allowedToPost && (
              <button
                onClick={() => setShowReportModal(true)}
                className="w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-xl transition-all duration-200 group relative"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                <div className="absolute right-16 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  Wellsite Activity Report
                </div>
              </button>
            )}
            <button
              ref={searchButtonRef}
              onClick={() => {
                setSearchDefaults(null);
                setShowSearchModal(true);
              }}
              className={`w-14 h-14 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center shadow-xl transition-all duration-200 group relative ${showTour && tourStep === 2 ? 'animate-pulse ring-4 ring-blue-300 ring-offset-2 ring-offset-black' : ''}`}
            >
              <Search className="w-6 h-6 text-white" />
              <div className="absolute right-16 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Active Search
              </div>
            </button>
          </div>
        </>
      )}

      {/* Wellsite Activity Report Modal */}
      {user?.allowedToPost && showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowReportModal(false)}
          ></div>
          
          <div className="relative bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-2xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Wellsite Activity Report</h2>
              <button
                onClick={() => setShowReportModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Activity
                </label>
                <select
                  value={reportForm.activityDefinitionId}
                  onChange={(e) => handleActivityChange(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  {Object.entries(groupedFieldReportActivities).map(([type, defs]) => (
                    <optgroup key={type} label={ACTIVITY_LABEL[type as ActivityType]}>
                      {defs.map(def => (
                        <option key={def.id} value={def.id}>
                          {def.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <input type="hidden" value={reportForm.activityType} hidden />

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <div className="relative">
                  <textarea
                    value={reportForm.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={2}
                    spellCheck={true}
                    className="w-full px-3 py-2 pr-12 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                  />
                  <button
                    type="button"
                    onClick={toggleVoiceRecognition}
                    className={`absolute bottom-3 right-3 p-2 rounded-full transition-all duration-200 ${
                      isListening
                        ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                        : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-700'
                    }`}
                    title={isListening ? 'Stop recording' : 'Start voice recording'}
                  >
                    {isListening ? (
                      <svg className="w-4 h-4 text-gray-900 dark:text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a2 2 0 114 0v4a2 2 0 11-4 0V7z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                    )}
                  </button>
                </div>
                {isListening && (
                  <p className="text-green-400 text-xs mt-1 flex items-center">
                    <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse mr-2"></span>
                    Listening... Speak now
                  </p>
                )}
                {speechRecognition === null && (
                  <p className="text-yellow-400 text-xs mt-1">
                    Voice recognition not supported in this browser. Try Chrome or Safari.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  value={reportForm.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  autoComplete="off"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Well
                </label>
                <input
                  type="text"
                  value={reportForm.well || ''}
                  onChange={(e) => handleInputChange('well', e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  autoComplete="off"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Location
                </label>

                <div className="relative">
                  <input
                    type="text"
                    value={reportForm.location}
                    className="w-full px-3 py-2 pr-12 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-white cursor-not-allowed"
                    readOnly
                    disabled
                  />
                  {locationStatus === 'pending' && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                    </div>
                  )}
                  {locationStatus === 'granted' && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
              {locationStatus === 'denied' && (
                <p className="mt-2 text-sm text-red-500">
                  Location access is required. Please enable location services in your browser settings.
                  <a
                    href="https://support.google.com/chrome/answer/142065"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-blue-400 ml-1"
                  >
                    Enable location
                  </a>
                </p>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Add Photo (Optional)
                </label>
                {!reportForm.photo ? (
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      id="photo-upload"
                    />
                    <label
                      htmlFor="photo-upload"
                      className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-500/5 transition-colors"
                    >
                      <div className="text-center">
                        <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <p className="text-gray-500 dark:text-gray-400 text-sm">Tap to take photo</p>
                      </div>
                    </label>
                  </div>
                ) : (
                  <div className="relative">
                    <img 
                      src={reportForm.photo} 
                      alt="Field report" 
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={removePhoto}
                      className="absolute top-2 right-2 p-1 bg-red-500 hover:bg-red-600 rounded-full text-white transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReportSubmit}
                  disabled={!canSubmit}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium ${
                    canSubmit
                      ? 'bg-green-500 hover:bg-green-600 text-white'
                      : 'bg-gray-300 text-gray-400 dark:bg-gray-600 dark:text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Submit
                </button>
              </div>
            </div>
            {locationStatus === 'denied' && (
              <div className="absolute inset-0 bg-black/80 rounded-2xl flex flex-col items-center justify-center">
                <p className="text-white mb-4">Enable location to submit.</p>
                <button
                  type="button"
                  onClick={request}
                  className="px-4 py-2 bg-green-500 rounded-lg hover:bg-green-600"
                >
                  Enable Location
                </button>
              </div>
            )}
          </div>
        </div>
      )}


      <ActiveSearchModal
        isOpen={showSearchModal}
        onClose={() => {
          setShowSearchModal(false);
          setSearchDefaults(null);
        }}
        savedSearches={savedSearches}
        loadSavedSearches={loadSavedSearches}
        apiCall={apiCall}
        user={user}
        setToast={setToast}
        toggleSearchStatus={toggleSavedSearchStatus}
        initialSearchForm={searchDefaults}
      />

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowProfileModal(false)}
          ></div>
          
          <div className="relative bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Profile</h2>
              <button
                onClick={() => setShowProfileModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Profile Navigation Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              {['Profile', 'Notifications', 'Preferences', 'Subscription'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setProfileActiveTab(tab)}
                  className={`flex-1 py-4 px-4 text-sm font-medium relative transition-colors ${
                    profileActiveTab === tab
                      ? 'text-gray-900 dark:text-white'
                      : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {tab}
                  {profileActiveTab === tab && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gray-900 dark:bg-white rounded-full"></div>
                  )}
                </button>
              ))}
            </div>

            {/* Profile Tab Content */}
            {profileActiveTab === 'Profile' && (
              <div className="p-6 space-y-6 min-h-[500px]">
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                    {user?.firstName?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors text-sm">
                    Change Photo
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First Name</label>
                    <input
                      type="text"
                      value={profileForm.firstName}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Last Name</label>
                    <input
                      type="text"
                      value={profileForm.lastName}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Smith"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Company</label>
                  <input
                    type="text"
                    value={profileForm.company}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, company: e.target.value }))}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ExxonMobil"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="john.smith@exxonmobil.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
                  <PhoneNumberInput
                    value={profileForm.phone || ''}
                    onChange={(val) => setProfileForm(prev => ({ ...prev, phone: val }))}
                    onBlur={validateProfilePhone}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {profilePhoneError && (
                    <p className="mt-1 text-sm text-red-500">{profilePhoneError}</p>
                  )}
                </div>



                <div className="flex space-x-3 pt-4">
                  <button onClick={handleProfileUpdate} className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium">
                    Save
                  </button>
                  <button onClick={handleLogout} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 transition-colors font-medium">
                    Logout
                  </button>
                </div>
              </div>
            )}

            {/* Notifications Tab Content */}
            {profileActiveTab === 'Notifications' && (
              <div className="p-6 space-y-6 min-h-[400px]">
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 dark:text-white">Enable Push Notifications</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={notificationPrefs.push}
                      onChange={handleNotificationToggle('push')}
                    />
                    <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                  </label>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Email Notifications</h3>
                  <div className="space-y-4 ml-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-900 dark:text-white">Real-time Alerts</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notificationPrefs.realTimeAlerts}
                          onChange={handleNotificationToggle('realTimeAlerts')}
                        />
                        <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-900 dark:text-white">Daily Summary</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notificationPrefs.dailySummary}
                          onChange={handleNotificationToggle('dailySummary')}
                        />
                        <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-900 dark:text-white">Weekly Report</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notificationPrefs.weeklyReport}
                          onChange={handleNotificationToggle('weeklyReport')}
                        />
                        <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preferences Tab Content */}
            {profileActiveTab === 'Preferences' && (
              <div className="p-6 space-y-6 min-h-[400px]">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Language</label>
                  <select
                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={language}
                    onChange={handleLanguageChange}
                  >
                    <option value="English">English</option>
                    <option value="Spanish">Spanish</option>
                  </select>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Activity Feed</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">Select the types of activity you want to see</p>
                  <div className="space-y-3">
                    {(Object.keys(ACTIVITY_LABEL) as ActivityType[]).map((value) => (
                      <div key={value} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-4 h-4 rounded-full ${ACTIVITY_COLOR[value]} cursor-pointer hover:scale-110 transition-transform`}></div>
                          <span className="text-gray-900 dark:text-white">{ACTIVITY_LABEL[value]}</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={selectedActivityTypes.has(value)}
                            onChange={(e) =>
                              setSelectedActivityTypes((prev) => {
                                const updatedSet = new Set(prev);
                                if (e.target.checked) {
                                  updatedSet.add(value);
                                } else {
                                  updatedSet.delete(value);
                                }
                                savePreferences({ activityFeed: Array.from(updatedSet) });
                                return updatedSet;
                              })
                            }
                          />
                          <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Subscription Tab Content */}
            {profileActiveTab === 'Subscription' && (
              <div className="p-6 space-y-6 min-h-[500px]">
                <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Current Plan</h3>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-gray-900 dark:text-black bg-white">
                      Active
                    </span>
                  </div>
                  <div className="space-y-2 text-gray-700 dark:text-gray-300">
                    <p>
                      Plan: <span className="text-gray-900 dark:text-white">Free</span>
                    </p>
                  </div>
                </div>

                <div className="pt-4">
                  <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium">
                    Upgrade
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && selectedActivityToShare && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowShareModal(false)}
          ></div>

          <div className="relative bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Share Activity</h2>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {/* Activity Preview */}
              <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  {shareActivityTag && (
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${shareActivityTag.color}`}>
                      {shareActivityTag.text}
                    </span>
                  )}
                  {shareCompanyName && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white bg-gray-700">
                      {shareCompanyName}
                    </span>
                  )}
                  {shareWellName && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white bg-gray-700">
                      {shareWellName}
                    </span>
                  )}
                  <span className="text-gray-500 text-xs">{selectedActivityToShare.time}</span>
                </div>
                <p className="text-gray-900 dark:text-white text-sm leading-relaxed whitespace-pre-wrap">
                  {selectedActivityToShare.description}
                </p>
                {selectedActivityToShare.photo && (
                  <div className="mt-2 mb-2">
                    {(() => {
                      const url = selectedActivityToShare.photo.replace('?size=medium', '');
                      return (
                        <img
                          src={`${url}?size=medium`}
                          srcSet={`${url}?size=thumb 400w, ${url}?size=medium 800w, ${url} 1920w`}
                          sizes="(max-width: 768px) 100vw, 768px"
                          loading="lazy"
                          alt="Field report"
                          className="w-full h-auto rounded-lg border border-gray-200 dark:border-gray-700"
                          onError={e => {
                            const img = e.currentTarget;
                            if (img.src.includes('?size=thumb') || img.src.includes('?size=medium')) {
                              img.onerror = null;
                              img.src = url;
                            }
                          }}
                        />
                      );
                    })()}
                  </div>
                )}
                <div className="flex items-center space-x-1 mt-2">
                  <MapPin className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-500 text-xs">{selectedActivityToShare.location}</span>
                </div>
              </div>

              {/* Share Options */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${selectedActivityToShare.description || ''} - ${selectedActivityToShare.location || ''}`);
                    alert('Activity copied to clipboard!');
                    setShowShareModal(false);
                  }}
                  className="w-full flex items-center space-x-3 p-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="text-gray-900 dark:text-white font-medium">Copy to clipboard</span>
                </button>

                <button
                  onClick={() => {
                    const subject = encodeURIComponent('Wellsite Activity Update');
                    const body = encodeURIComponent(`${selectedActivityToShare.description || ''}\n\nLocation: ${selectedActivityToShare.location || ''}\nSource: ${selectedActivityToShare.source ? DATA_SOURCE_LABEL[selectedActivityToShare.source] : ''}`);
                    window.open(`mailto:?subject=${subject}&body=${body}`);
                    setShowShareModal(false);
                  }}
                  className="w-full flex items-center space-x-3 p-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-gray-900 dark:text-white font-medium">Share via email</span>
                </button>

                <button
                  onClick={() => {
                    const text = encodeURIComponent(`${selectedActivityToShare.description || ''} - ${selectedActivityToShare.location || ''}`);
                    window.open(`sms:?body=${text}`);
                    setShowShareModal(false);
                  }}
                  className="w-full flex items-center space-x-3 p-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m-2 8l-6-6V5a2 2 0 012-2h12a2 2 0 012 2v9a2 2 0 01-2 2H9l-2 2z" />
                  </svg>
                  <span className="text-gray-900 dark:text-white font-medium">Share via SMS</span>
                </button>

                <button
                  onClick={() => {
                    const url = encodeURIComponent(window.location.href);
                    const text = encodeURIComponent(`${selectedActivityToShare.description || ''} - ${selectedActivityToShare.location || ''}`);
                    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}&text=${text}`, '_blank');
                    setShowShareModal(false);
                  }}
                  className="w-full flex items-center space-x-3 p-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-900 dark:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  <span className="text-gray-900 dark:text-white font-medium">Share on LinkedIn</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Modal */}
      {showNotificationsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowNotificationsModal(false)}
          ></div>

          <div className="relative bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Notifications</h2>
              <button
                onClick={() => setShowNotificationsModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4">
              <div className="space-y-3">
                {!user?.emailVerifiedAt && (
                  <div className="p-4 mb-2 bg-yellow-50 dark:bg-yellow-900/50 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                    <p className="text-yellow-700 dark:text-yellow-100 text-sm mb-2">
                      Your email is not verified. Please verify to access all features.
                    </p>
                    <button
                      onClick={handleResendVerification}
                      disabled={resendCooldown > 0}
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm"
                    >
                      {resendCooldown > 0
                        ? `Resend in ${resendCooldown}s`
                        : 'Resend Verification Email'}
                    </button>
                  </div>
                )}
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => markNotificationAsRead(notification.id)}
                    className={`p-4 rounded-lg border cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900/50 transition-colors ${
                      notification.isRead
                        ? 'bg-gray-50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700'
                        : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className={`font-medium ${notification.isRead ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-white'}`}>
                            {notification.title}
                          </h3>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                        <p className={`text-sm ${notification.isRead ? 'text-gray-600 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">{formatTimeAgo(notification.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {notifications.length === 0 && (
                  <p className="text-gray-700 dark:text-gray-400 text-sm">No notifications</p>
                )}
              </div>

              {/* Actions */}
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex space-x-3">
                  <button onClick={markAllNotificationsAsRead} className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium text-sm">
                    Mark All Read
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default WellsiteActivityFeed;
